import db from "./offlineDb";
import { getOfflineContext, getOfflineTermId, setStoredOfflineContext } from "./offlineContext";

function withNamespace(state, extra = {}) {
  const ctx = getOfflineContext(state);
  return {
    schoolId: ctx.schoolId ?? null,
    userId: ctx.userId ?? null,
    sessionId: extra.sessionId ?? ctx.sessionId ?? null,
    termId: extra.termId ?? ctx.termId ?? null,
    ...extra,
  };
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function parseCommentType(body) {
  return body?.type || "term";
}

function normalizeCommentTitle(title) {
  return String(title || "").trim().replace(/\s+/g, " ");
}

async function touchPreloadStatus(state, classroomId, patch = {}) {
  if (!classroomId) return;
  const ctx = getOfflineContext(state);
  const termId = patch.termId ?? ctx.termId ?? null;
  if (!ctx.userId || !ctx.schoolId || !classroomId || !termId) return;
  const key = [ctx.schoolId, ctx.userId, termId, classroomId];
  const existing = await db.preloadStatus.get(key);
  await db.preloadStatus.put({
    schoolId: ctx.schoolId,
    userId: ctx.userId,
    termId,
    classroomId,
    sessionId: patch.sessionId ?? existing?.sessionId ?? ctx.sessionId ?? null,
    status: patch.status ?? existing?.status ?? "cached",
    stale: patch.stale ?? existing?.stale ?? false,
    lastFetchedAt: patch.lastFetchedAt ?? existing?.lastFetchedAt ?? null,
    lastValidatedAt: patch.lastValidatedAt ?? existing?.lastValidatedAt ?? Date.now(),
    recordCount: patch.recordCount ?? existing?.recordCount ?? 0,
    commentCount: patch.commentCount ?? existing?.commentCount ?? 0,
    traitGradeCount: patch.traitGradeCount ?? existing?.traitGradeCount ?? 0,
    error: patch.error ?? existing?.error ?? null,
  });
}

export async function applyOfflineMutationToCache(args, state) {
  const rawUrl = typeof args === "string" ? args : args?.url ?? "";
  const url = rawUrl.replace(/^\//, "");
  const method = (typeof args === "object" ? args?.method : null) ?? "POST";
  const body = typeof args === "object" ? args?.body ?? {} : {};
  const termId = body?.term_id ?? getOfflineTermId(state);
  const classroomId = body?.classroom_id ?? null;

  if (termId) {
    setStoredOfflineContext({ termId });
  }

  if ((url.startsWith("record/create") || (url.startsWith("record") && method === "POST")) && classroomId && body?.subject_id) {
    const namespace = withNamespace(state, {
      classroomId,
      subjectId: body.subject_id,
      termId,
    });
    const key = [namespace.schoolId, namespace.userId, namespace.termId, namespace.classroomId, namespace.subjectId];
    const existing = await db.records.get(key);
    const existingRows = asArray(existing?.data);
    const recordMap = new Map(
      existingRows.map((row) => [`${row.student_id}:${row.assessment_id}`, row])
    );

    for (const record of asArray(body.records)) {
      recordMap.set(`${record.student_id}:${record.assessment_id}`, {
        ...recordMap.get(`${record.student_id}:${record.assessment_id}`),
        ...record,
        subject_id: body.subject_id,
        classroom_id: classroomId,
        term_id: namespace.termId,
        _offline: true,
        _offlineUpdatedAt: new Date().toISOString(),
      });
    }

    await db.records.put({
      ...namespace,
      data: [...recordMap.values()],
      cachedAt: Date.now(),
    });
    await touchPreloadStatus(state, classroomId, {
      termId: namespace.termId,
      status: "cached",
      stale: true,
      recordCount: recordMap.size,
      lastValidatedAt: Date.now(),
    });
    return;
  }

  if ((url.startsWith("comment/create") || (url.startsWith("comment") && method === "POST" && !url.includes("ai"))) && classroomId) {
    const commentType = parseCommentType(body);
    const comments = asArray(body.comments);
    const updatesByStudent = new Map(comments.map((item) => [String(item.student_id), item]));
    const nowIso = new Date().toISOString();

    if (body.subject_id) {
      const namespace = withNamespace(state, {
        classroomId,
        subjectId: body.subject_id,
        type: `subject:${body.subject_id}:${commentType}`,
        termId,
      });
      const key = [namespace.schoolId, namespace.userId, namespace.termId, 0, namespace.classroomId, 0, 0, namespace.type];
      const existing = await db.comments.get(key);
      const merged = asArray(existing?.data)
        .filter((item) => !updatesByStudent.has(String(item.student_id)))
        .concat(
          comments.map((item) => ({
            ...item,
            subject_id: body.subject_id,
            classroom_id: classroomId,
            term_id: namespace.termId,
            type: commentType,
            _offline: true,
            updated_at: item.updated_at || nowIso,
          }))
        );

      await db.comments.put({
        ...namespace,
        studentId: 0,
        sectionId: 0,
        departmentId: 0,
        data: merged,
        cachedAt: Date.now(),
      });
    } else {
      for (const item of comments) {
        const namespace = withNamespace(state, {
          classroomId,
          sectionId: body.section_id ?? 0,
          departmentId: body.department_id ?? 0,
          studentId: item.student_id,
          type: commentType,
          termId,
        });
        const key = [
          namespace.schoolId,
          namespace.userId,
          namespace.termId,
          namespace.studentId,
          namespace.classroomId,
          namespace.sectionId ?? 0,
          namespace.departmentId ?? 0,
          namespace.type,
        ];
        const existing = await db.comments.get(key);
        const otherRows = asArray(existing?.data).filter(
          (row) => row.title !== item.title
        );
        await db.comments.put({
          ...namespace,
          subjectId: 0,
          data: [
            ...otherRows,
            {
              ...item,
              classroom_id: classroomId,
              section_id: body.section_id ?? 0,
              department_id: body.department_id ?? 0,
              term_id: namespace.termId,
              type: commentType,
              _offline: true,
              updated_at: item.updated_at || nowIso,
            },
          ],
          cachedAt: Date.now(),
        });
      }
    }

    await touchPreloadStatus(state, classroomId, {
      termId,
      status: "cached",
      stale: true,
      commentCount: comments.length,
      lastValidatedAt: Date.now(),
    });
    return;
  }

  if (url.startsWith("comment") && method === "DELETE" && classroomId && body?.student_id) {
    const commentType = parseCommentType(body);
    const title = body.title == null ? null : normalizeCommentTitle(body.title);

    if (body.subject_id) {
      const namespace = withNamespace(state, {
        classroomId,
        subjectId: body.subject_id,
        type: `subject:${body.subject_id}:${commentType}`,
        termId,
      });
      const key = [namespace.schoolId, namespace.userId, namespace.termId, 0, namespace.classroomId, 0, 0, namespace.type];
      const existing = await db.comments.get(key);
      const nextRows = asArray(existing?.data).filter((row) => {
        if (String(row.student_id) !== String(body.student_id)) return true;
        if (title == null) return false;
        return normalizeCommentTitle(row.title) !== title;
      });

      if (existing) {
        await db.comments.put({
          ...existing,
          data: nextRows,
          cachedAt: Date.now(),
        });
      }
    } else {
      const namespace = withNamespace(state, {
        classroomId,
        sectionId: body.section_id ?? 0,
        departmentId: body.department_id ?? 0,
        studentId: body.student_id,
        type: commentType,
        termId,
      });
      const key = [
        namespace.schoolId,
        namespace.userId,
        namespace.termId,
        namespace.studentId,
        namespace.classroomId,
        namespace.sectionId ?? 0,
        namespace.departmentId ?? 0,
        namespace.type,
      ];
      const existing = await db.comments.get(key);
      const nextRows = title == null
        ? []
        : asArray(existing?.data).filter((row) => normalizeCommentTitle(row.title) !== title);

      if (existing) {
        await db.comments.put({
          ...existing,
          data: nextRows,
          cachedAt: Date.now(),
        });
      }
    }

    await touchPreloadStatus(state, classroomId, {
      termId,
      status: "cached",
      stale: true,
      lastValidatedAt: Date.now(),
    });
    return;
  }

  if ((url.startsWith("trait/grade/create") || (url.startsWith("trait/grade") && method === "POST")) && classroomId && body?.student_id) {
    const namespace = withNamespace(state, {
      classroomId,
      studentId: body.student_id,
      termId,
    });
    const key = [namespace.schoolId, namespace.userId, namespace.termId, namespace.classroomId, namespace.studentId];
    const nowIso = new Date().toISOString();

    await db.traitGrades.put({
      ...namespace,
      data: asArray(body.grades).map((grade) => ({
        ...grade,
        student_id: body.student_id,
        classroom_id: classroomId,
        term_id: namespace.termId,
        _offline: true,
        updated_at: grade.updated_at || nowIso,
      })),
      cachedAt: Date.now(),
    });
    await touchPreloadStatus(state, classroomId, {
      termId: namespace.termId,
      status: "cached",
      stale: true,
      traitGradeCount: asArray(body.grades).length,
      lastValidatedAt: Date.now(),
    });
  }
}
